import { ComponentProps, useEffect, useMemo, useState } from 'react';
import { Dropdown } from '@getsynapse/design-system';
import { Option } from 'utils/customTypes';

type DropdownProps = ComponentProps<typeof Dropdown>;

interface NullableDropdownProps extends DropdownProps {
  nullLabel?: string;
}

const NullableDropdown = ({
  nullLabel = 'Select None', // Default value for nullLabel
  ...otherProps
}: NullableDropdownProps) => {
  const { values, options = [], onChange } = otherProps;

  const [filterValue, setFilterValue] = useState('');
  const [displayedOptions, setDisplayedOptions] = useState<Option[]>(options);

  useEffect(() => {
    if (values?.length) {
      setFilterValue(values[0].label);
    } else {
      setFilterValue('');
    }
  }, [values]);

  useEffect(() => {
    setDisplayedOptions(options);
  }, [options]);

  const handleFilter = (input: string) => {
    const normalized = input.toLowerCase();

    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(normalized)
    );
    setDisplayedOptions(filtered);
  };

  // Create a new options array with the "Select None" option
  const updatedOptions = useMemo(() => {
    return values?.length
      ? [{ label: nullLabel, value: null }, ...displayedOptions]
      : displayedOptions;
  }, [displayedOptions, nullLabel, values]);

  return (
    <Dropdown
      {...otherProps}
      options={updatedOptions}
      triggerProps={{
        ...otherProps.triggerProps,
        onInputChange: (value: string) => {
          setFilterValue(value);
          handleFilter(value);
        },
        filterValue,
      }}
      onChange={(option: Option) => {
        setDisplayedOptions(options);
        onChange(option);
      }}
    />
  );
};

export default NullableDropdown;
